<script setup lang="ts">
import { useRtl } from '@shared/composables';
import type { LinkItem, Media } from '~/types/api';
import ArrowIcon from '~/libs/shared/ui/src/components/icons/ArrowIcon.vue';

const { isRtl } = useRtl();
const openItem = ref<number>(-1);

interface IncludeCard {
  icon: Media;
  title: string;
  description: string;
  addon_badge: boolean;
}

interface Tab {
  id: string;
  title: string;
  includes_cards: IncludeCard[];
}

const props = defineProps<{
  title: string;
  tabs: Tab[];
  bottom_link: LinkItem;
}>();

const activeTab = ref<string>(props?.tabs?.[0].id);

const currentTab = computed(() => props?.tabs?.find(tab => tab?.id === activeTab.value) || null);

function openTab (id: string) {
  activeTab.value = id;
}

const toggleItem = (index: number) => {
  if (openItem.value === index) { openItem.value = -1; } else { openItem.value = index; }
};
</script>

<template>
  <section class="landing-includes section-offset" dir="auto">
    <div class="container">
      <h3 v-html="props.title" />
      <div class="landing-includes__menu">
        <div v-for="tab in props?.tabs || []" :key="tab.id" class="landing-includes__menu-item" :class="{is_active: activeTab === tab.id}" @click.prevent="openTab(tab?.id)">
          <span>{{ tab?.title }}</span>
        </div>
      </div>
      <div class="landing-includes__wrapper" :class="{two_options: currentTab?.includes_cards?.length === 2}">
        <div
          v-for="item in currentTab?.includes_cards || []"
          :key="item.title"
          :class="[
            'landing-includes__item',
            {
              'landing-includes__item--small': !item.icon,
            },
          ]"
        >
          <div v-if="item?.icon && item?.icon" class="landing-includes__item-icon">
            <img v-if="item?.icon?.url" :src="item?.icon?.url" alt="" loading="lazy">
          </div>
          <h4>
            {{ item?.title }}
            <span v-if="item?.addon_badge">{{ $t('main.includes.addon') }}</span>
          </h4>
          <p>
            {{ item?.description }}
          </p>
        </div>
      </div>
      <div class="landing-includes__collapse collapse">
        <div
          v-for="(item, index) in currentTab?.includes_cards" :key="item.title" class="collapse__item"
          :class="{ is_active: openItem === index }" @click="toggleItem(index)"
        >
          <div class="collapse__header">
            <ArrowIcon />
            <h4>
              {{ item.title }}
            </h4>
            <span v-if="item.addon_badge">{{ $t('main.includes.addon') }}</span>
          </div>
          <transition name="fade-collapse">
            <div v-show="openItem === index" class="collapse__body">
              <div @click.stop v-html="item.description" />
            </div>
          </transition>
        </div>
      </div>
      <div v-if="props?.bottom_link?.href" class="landing-includes__downloads">
        <a :href="props?.bottom_link?.href" :target="props?.bottom_link?.target" class="btn dc-link-button">
          {{ props?.bottom_link?.title }}
          <img v-if="props?.bottom_link?.icon" :src="props?.bottom_link?.icon?.url">
        </a>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import './IncludesCarBlock.scss';
</style>
